/**
 * Types and pure functions for mosaic layout drag-and-drop with drop targets.
 *
 * When dragging a widget, drop zones appear on other widgets (top/bottom/left/right/center).
 * Hovering over edge zones shows a preview of how space will be split.
 * Hovering over the center zone swaps the two widgets' positions.
 * Dropping commits the operation, updating both the dragged and target widget layouts.
 *
 * Enhanced drop targets support shared edges between columns/rows, providing
 * N-way redistribution when dropping into existing structures.
 */
import type { SharedEdge, InferredColumn, InferredRow, LayoutInferenceState } from './mosaic-layout-inference';

export type DropDirection = 'top' | 'bottom' | 'left' | 'right' | 'center';

/**
 * Drag mode determines how drop targets are detected:
 * - 'default': Only edge zones trigger split operations (center zone returns null)
 * - 'shift': Entire widget is a swap target (always returns 'center' when inside a widget)
 */
export type DragMode = 'default' | 'shift';

/** Edge zone size as fraction of widget dimension (25% = edges, 50% center) */
const EDGE_ZONE_SIZE = 0.25;

export interface DropTarget {
    targetContainerId: string;
    direction: DropDirection;
}

export interface NormalizedLayout {
    x: number;
    y: number;
    w: number;
    h: number;
}

export interface DropPreviewLayout {
    draggedLayout: NormalizedLayout;
    targetLayout: NormalizedLayout;
}

export interface MosaicDragState {
    draggedContainerId: string;
    originalLayout: NormalizedLayout;
    activeDropTarget: DropTarget | null;
    previewLayouts: DropPreviewLayout | null;
}

/**
 * Enhanced drop target that may include shared edge information.
 * When a shared edge is detected, the drop will redistribute space
 * among all widgets in the column/row.
 */
export interface EnhancedDropTarget extends DropTarget {
    /** The shared edge being targeted, if any */
    sharedEdge: SharedEdge | null;
    /** The column containing this target, if applicable */
    column: InferredColumn | null;
    /** The row containing this target, if applicable */
    row: InferredRow | null;
}

/**
 * Drop preview for multi-widget operations.
 * Used when dropping into a column/row redistributes space among N widgets.
 */
export interface MultiWidgetDropPreview {
    /** Layouts for all affected widgets, keyed by container ID */
    layouts: Record<string, NormalizedLayout>;
}

/**
 * A precomputed drop scenario - one possible outcome of a drop operation.
 */
export interface DropScenario {
    /** The target widget being dropped onto */
    targetContainerId: string;
    /** The drop direction/zone */
    direction: DropDirection;
    /** The resulting layouts for all affected widgets */
    resultLayouts: Record<string, NormalizedLayout>;
}

/**
 * All precomputed drop scenarios for a drag operation.
 */
export interface PrecomputedDropScenarios {
    /** Map from "targetId:direction" to the scenario */
    scenarios: Map<string, DropScenario>;
    /** The dragged widget ID */
    draggedId: string;
}

/** Minimum size for a widget (6% of viewport, aligns with 2% grid) */
export const MIN_SIZE = 0.06;

/** Grid snap increment for mosaic layout (2% = 0.02). Always on during drag/resize in mosaic mode. */
export const MOSAIC_GRID_INCREMENT = 0.02;

// Reusable result object for findActiveDropTarget
const dropTargetResult: DropTarget = { targetContainerId: '', direction: 'top' };

/**
 * Find which drop target (if any) the mouse is hovering over.
 *
 * For each widget (except the one being dragged), we check if the mouse is inside.
 * If inside, we find the closest edge to determine the drop direction.
 *
 * @param mouseX - Mouse X position normalized (0-1)
 * @param mouseY - Mouse Y position normalized (0-1)
 * @param layouts - Current layouts keyed by container ID
 * @param draggedContainerId - Container ID of the widget being dragged
 * @returns The active drop target, or null if not hovering over any
 */
export function findActiveDropTarget(
    mouseX: number,
    mouseY: number,
    layouts: Record<string, NormalizedLayout>,
    draggedContainerId: string,
): DropTarget | null {
    for (const containerId in layouts) {
        if (containerId === draggedContainerId) continue;

        const layout = layouts[containerId];
        const { x, y, w, h } = layout;

        // Check if mouse is inside this widget
        if (mouseX >= x && mouseX <= x + w && mouseY >= y && mouseY <= y + h) {
            // Mouse is inside - find the drop zone
            const relX = (mouseX - x) / w; // 0-1 within widget
            const relY = (mouseY - y) / h; // 0-1 within widget

            // Check if in center zone (not within edge threshold of any edge)
            const inCenterX = relX > EDGE_ZONE_SIZE && relX < 1 - EDGE_ZONE_SIZE;
            const inCenterY = relY > EDGE_ZONE_SIZE && relY < 1 - EDGE_ZONE_SIZE;

            let direction: DropDirection;

            if (inCenterX && inCenterY) {
                // In center zone - swap operation
                direction = 'center';
            } else {
                // In edge zone - find closest edge
                const distTop = relY;
                const distBottom = 1 - relY;
                const distLeft = relX;
                const distRight = 1 - relX;

                direction = 'top';
                let minDist = distTop;

                if (distBottom < minDist) {
                    minDist = distBottom;
                    direction = 'bottom';
                }
                if (distLeft < minDist) {
                    minDist = distLeft;
                    direction = 'left';
                }
                if (distRight < minDist) {
                    direction = 'right';
                }
            }

            // Reuse result object
            dropTargetResult.targetContainerId = containerId;
            dropTargetResult.direction = direction;
            return dropTargetResult;
        }
    }
    return null;
}

/**
 * Find which drop target (if any) the mouse is hovering over, with mode-aware behavior.
 *
 * In default mode: Only edge zones trigger split operations (hovering in center zone returns null).
 * In shift mode: Entire widget becomes a swap target (always returns 'center' when inside a widget).
 *
 * @param mouseX - Mouse X position normalized (0-1)
 * @param mouseY - Mouse Y position normalized (0-1)
 * @param layouts - Current layouts keyed by container ID
 * @param draggedContainerId - Container ID of the widget being dragged
 * @param mode - Drag mode ('default' for edge-only, 'shift' for swap mode)
 * @returns The active drop target, or null if not hovering over any
 */
export function findActiveDropTargetForMode(
    mouseX: number,
    mouseY: number,
    layouts: Record<string, NormalizedLayout>,
    draggedContainerId: string,
    mode: DragMode,
): DropTarget | null {
    for (const containerId in layouts) {
        if (containerId === draggedContainerId) continue;

        const layout = layouts[containerId];
        const { x, y, w, h } = layout;

        // Check if mouse is inside this widget
        if (mouseX >= x && mouseX <= x + w && mouseY >= y && mouseY <= y + h) {
            if (mode === 'shift') {
                // Shift mode: entire widget is swap target
                dropTargetResult.targetContainerId = containerId;
                dropTargetResult.direction = 'center';
                return dropTargetResult;
            }

            // Default mode: edge zones only, center zone returns null
            const relX = (mouseX - x) / w; // 0-1 within widget
            const relY = (mouseY - y) / h; // 0-1 within widget

            // Check if in center zone (not within edge threshold of any edge)
            const inCenterX = relX > EDGE_ZONE_SIZE && relX < 1 - EDGE_ZONE_SIZE;
            const inCenterY = relY > EDGE_ZONE_SIZE && relY < 1 - EDGE_ZONE_SIZE;

            if (inCenterX && inCenterY) {
                // In default mode, center zone doesn't trigger any drop target
                return null;
            }

            // In edge zone - find closest edge
            const distTop = relY;
            const distBottom = 1 - relY;
            const distLeft = relX;
            const distRight = 1 - relX;

            let direction: DropDirection = 'top';
            let minDist = distTop;

            if (distBottom < minDist) {
                minDist = distBottom;
                direction = 'bottom';
            }
            if (distLeft < minDist) {
                minDist = distLeft;
                direction = 'left';
            }
            if (distRight < minDist) {
                direction = 'right';
            }

            dropTargetResult.targetContainerId = containerId;
            dropTargetResult.direction = direction;
            return dropTargetResult;
        }
    }
    return null;
}

/**
 * Compute the preview layouts for a drop operation.
 *
 * For edge drops (top/bottom/left/right): the space is split in half.
 * The dragged widget takes one half, the target widget takes the other half.
 *
 * For center drops: the widgets swap positions entirely.
 *
 * @param targetLayout - Current layout of the target widget
 * @param direction - The drop direction (which zone of target was hovered)
 * @param draggedLayout - Original layout of the dragged widget (needed for swap)
 * @returns Preview layouts for both widgets
 */
export function computeDropPreview(
    targetLayout: NormalizedLayout,
    direction: DropDirection,
    draggedLayout: NormalizedLayout,
): DropPreviewLayout {
    const { x, y, w, h } = targetLayout;

    // Center = swap positions (snap both layouts)
    if (direction === 'center') {
        return {
            draggedLayout: snapLayoutToMosaicGrid({ x, y, w, h }),
            targetLayout: snapLayoutToMosaicGrid({
                x: draggedLayout.x,
                y: draggedLayout.y,
                w: draggedLayout.w,
                h: draggedLayout.h,
            }),
        };
    }

    // Edge drops = split in half, snapped to mosaic grid
    // Use distributeSpaceProportionally for even split
    const snappedX = snapToMosaicGrid(x);
    const snappedY = snapToMosaicGrid(y);
    const snappedW = snapToMosaicGrid(w);
    const snappedH = snapToMosaicGrid(h);

    switch (direction) {
        case 'top':
        case 'bottom': {
            // Distribute height evenly between two widgets
            const heights = distributeSpaceProportionally(snappedH, [1, 1]);
            const topH = heights[0];
            const bottomH = heights[1];
            if (direction === 'top') {
                // Dragged widget goes to top half, target moves to bottom half
                return {
                    draggedLayout: { x: snappedX, y: snappedY, w: snappedW, h: topH },
                    targetLayout: { x: snappedX, y: snappedY + topH, w: snappedW, h: bottomH },
                };
            } else {
                // Dragged widget goes to bottom half, target stays in top half
                return {
                    draggedLayout: { x: snappedX, y: snappedY + topH, w: snappedW, h: bottomH },
                    targetLayout: { x: snappedX, y: snappedY, w: snappedW, h: topH },
                };
            }
        }
        case 'left':
        case 'right': {
            // Distribute width evenly between two widgets
            const widths = distributeSpaceProportionally(snappedW, [1, 1]);
            const leftW = widths[0];
            const rightW = widths[1];
            if (direction === 'left') {
                // Dragged widget goes to left half, target moves to right half
                return {
                    draggedLayout: { x: snappedX, y: snappedY, w: leftW, h: snappedH },
                    targetLayout: { x: snappedX + leftW, y: snappedY, w: rightW, h: snappedH },
                };
            } else {
                // Dragged widget goes to right half, target stays in left half
                return {
                    draggedLayout: { x: snappedX + leftW, y: snappedY, w: rightW, h: snappedH },
                    targetLayout: { x: snappedX, y: snappedY, w: leftW, h: snappedH },
                };
            }
        }
    }
}

/**
 * Apply grid snapping to a layout value.
 *
 * @param value - The value to snap
 * @param gridSize - The grid size to snap to (default: 0.01 = 1%)
 * @returns The snapped value
 */
export function snapToGrid(value: number, gridSize = 0.01): number {
    return Math.round(value / gridSize) * gridSize;
}

/**
 * Apply grid snapping to an entire layout.
 *
 * @param layout - The layout to snap
 * @param gridSize - The grid size to snap to (default: 0.01 = 1%)
 * @returns The snapped layout
 */
export function snapLayoutToGrid(layout: NormalizedLayout, gridSize = 0.01): NormalizedLayout {
    return {
        x: snapToGrid(layout.x, gridSize),
        y: snapToGrid(layout.y, gridSize),
        w: snapToGrid(layout.w, gridSize),
        h: snapToGrid(layout.h, gridSize),
    };
}

/**
 * Snap a value to the mosaic grid (5% increments).
 * Used unconditionally during mosaic drag/resize operations.
 *
 * @param value - The value to snap
 * @returns The snapped value
 */
export function snapToMosaicGrid(value: number): number {
    return Math.round(value / MOSAIC_GRID_INCREMENT) * MOSAIC_GRID_INCREMENT;
}

/**
 * Ceil a value up to the nearest mosaic grid increment.
 * Used to round preferred widget dimensions up so they align to the grid.
 *
 * @param value - The value to ceil
 * @returns The ceiled value (always >= input, on grid)
 */
export function ceilToMosaicGrid(value: number): number {
    return Math.ceil(value / MOSAIC_GRID_INCREMENT) * MOSAIC_GRID_INCREMENT;
}

/**
 * Distribute total space proportionally among widgets, with all sizes snapped to the mosaic grid.
 * Maintains quasi-proportional distribution based on current sizes while respecting minimum size.
 *
 * @param totalSpace - Total space to distribute (should be a multiple of MOSAIC_GRID_INCREMENT)
 * @param currentSizes - Current sizes of widgets (used to determine proportions)
 * @returns Array of sizes that sum to totalSpace, each a multiple of MOSAIC_GRID_INCREMENT and >= MIN_SIZE
 */
export function distributeSpaceProportionally(totalSpace: number, currentSizes: number[]): number[] {
    const count = currentSizes.length;
    if (count === 0) return [];
    if (count === 1) return [snapToMosaicGrid(totalSpace)];

    const totalIncrements = Math.round(totalSpace / MOSAIC_GRID_INCREMENT);
    const minIncrements = Math.round(MIN_SIZE / MOSAIC_GRID_INCREMENT); // = 1

    // Calculate current total to get proportions
    const currentTotal = currentSizes.reduce((sum, s) => sum + s, 0);

    // Calculate ideal proportional sizes and snap to increments
    const idealIncrements: number[] = [];
    const roundingErrors: number[] = []; // Track how much each was rounded (positive = rounded down)

    for (const size of currentSizes) {
        const proportion = currentTotal > 0 ? size / currentTotal : 1 / count;
        const idealIncrement = proportion * totalIncrements;
        const snappedIncrement = Math.max(minIncrements, Math.round(idealIncrement));
        idealIncrements.push(snappedIncrement);
        roundingErrors.push(idealIncrement - snappedIncrement); // positive means we rounded down
    }

    // Calculate how many increments we're off by
    const currentIncrementTotal = idealIncrements.reduce((sum, i) => sum + i, 0);
    let incrementDelta = totalIncrements - currentIncrementTotal;

    // Distribute the delta based on rounding errors
    // If incrementDelta > 0, we need to add increments (prefer those that were rounded down most)
    // If incrementDelta < 0, we need to remove increments (prefer those that were rounded up most)
    while (incrementDelta !== 0) {
        if (incrementDelta > 0) {
            // Find widget that was rounded down the most (largest positive error)
            let bestIdx = 0;
            let bestError = -Infinity;
            for (let i = 0; i < count; i++) {
                if (roundingErrors[i] > bestError) {
                    bestError = roundingErrors[i];
                    bestIdx = i;
                }
            }
            idealIncrements[bestIdx]++;
            roundingErrors[bestIdx]--; // Adjust error since we added an increment
            incrementDelta--;
        } else {
            // Find widget that was rounded up the most (largest negative error) and can still shrink
            let bestIdx = -1;
            let bestError = Infinity;
            for (let i = 0; i < count; i++) {
                if (idealIncrements[i] > minIncrements && roundingErrors[i] < bestError) {
                    bestError = roundingErrors[i];
                    bestIdx = i;
                }
            }
            if (bestIdx === -1) break; // Can't shrink any further without violating minimum
            idealIncrements[bestIdx]--;
            roundingErrors[bestIdx]++; // Adjust error since we removed an increment
            incrementDelta++;
        }
    }

    // Convert back to sizes
    return idealIncrements.map((inc) => inc * MOSAIC_GRID_INCREMENT);
}

/**
 * Snap an entire layout to the mosaic grid (5% increments).
 * Used unconditionally during mosaic drag/resize operations.
 *
 * @param layout - The layout to snap
 * @returns The snapped layout
 */
export function snapLayoutToMosaicGrid(layout: NormalizedLayout): NormalizedLayout {
    return {
        x: snapToMosaicGrid(layout.x),
        y: snapToMosaicGrid(layout.y),
        w: snapToMosaicGrid(layout.w),
        h: snapToMosaicGrid(layout.h),
    };
}

/**
 * Check if two drop targets are equal.
 *
 * @param a - First drop target (or null)
 * @param b - Second drop target (or null)
 * @returns True if targets are equal
 */
export function dropTargetsEqual(a: DropTarget | null, b: DropTarget | null): boolean {
    if (a === null && b === null) return true;
    if (a === null || b === null) return false;
    return a.targetContainerId === b.targetContainerId && a.direction === b.direction;
}

// ========== ENHANCED DROP TARGET DETECTION ==========
// These functions support shared edge detection for N-way redistribution.

/** Reusable result object for findEnhancedDropTarget */
const enhancedDropTargetResult: EnhancedDropTarget = {
    targetContainerId: '',
    direction: 'top',
    sharedEdge: null,
    column: null,
    row: null,
};

/**
 * Check if a mouse position is near a shared edge.
 *
 * @param mouseX - Mouse X position normalized (0-1)
 * @param mouseY - Mouse Y position normalized (0-1)
 * @param edge - The shared edge to check
 * @param layouts - Current layouts keyed by container ID
 * @returns True if mouse is within the edge zone
 */
function isMouseNearSharedEdge(
    mouseX: number,
    mouseY: number,
    edge: SharedEdge,
    layouts: Record<string, NormalizedLayout>,
): boolean {
    if (edge.orientation === 'horizontal') {
        // Horizontal edge - check if mouseY is near the edge position
        const widgetA = layouts[edge.widgetA];
        const widgetB = layouts[edge.widgetB];
        if (!widgetA || !widgetB) return false;

        // Edge zone is based on adjacent widget heights
        const zoneAbove = widgetA.h * EDGE_ZONE_SIZE;
        const zoneBelow = widgetB.h * EDGE_ZONE_SIZE;

        const inYZone = mouseY >= edge.position - zoneAbove && mouseY <= edge.position + zoneBelow;
        const inXRange = mouseX >= edge.start && mouseX <= edge.end;

        return inYZone && inXRange;
    } else {
        // Vertical edge - check if mouseX is near the edge position
        const widgetA = layouts[edge.widgetA];
        const widgetB = layouts[edge.widgetB];
        if (!widgetA || !widgetB) return false;

        // Edge zone is based on adjacent widget widths
        const zoneLeft = widgetA.w * EDGE_ZONE_SIZE;
        const zoneRight = widgetB.w * EDGE_ZONE_SIZE;

        const inXZone = mouseX >= edge.position - zoneLeft && mouseX <= edge.position + zoneRight;
        const inYRange = mouseY >= edge.start && mouseY <= edge.end;

        return inXZone && inYRange;
    }
}

/**
 * Find the shared edge target at the mouse position, if any.
 *
 * @param mouseX - Mouse X position normalized (0-1)
 * @param mouseY - Mouse Y position normalized (0-1)
 * @param inferenceState - Layout inference state with shared edges
 * @param layouts - Current layouts keyed by container ID
 * @param draggedId - Container ID of the widget being dragged
 * @returns The shared edge and associated column/row, or null
 */
function findSharedEdgeTarget(
    mouseX: number,
    mouseY: number,
    inferenceState: LayoutInferenceState,
    layouts: Record<string, NormalizedLayout>,
    draggedId: string,
): { edge: SharedEdge; column: InferredColumn | null; row: InferredRow | null } | null {
    // Check each shared edge (excluding edges involving the dragged widget)
    for (const edge of inferenceState.sharedEdges) {
        if (edge.widgetA === draggedId || edge.widgetB === draggedId) continue;

        if (isMouseNearSharedEdge(mouseX, mouseY, edge, layouts)) {
            // Find the column or row containing this edge
            let column: InferredColumn | null = null;
            let row: InferredRow | null = null;

            if (edge.orientation === 'horizontal') {
                column = inferenceState.columns.find((c) => c.internalEdges.includes(edge)) ?? null;
            } else {
                row = inferenceState.rows.find((r) => r.internalEdges.includes(edge)) ?? null;
            }

            return { edge, column, row };
        }
    }

    return null;
}

/**
 * Find the enhanced drop target at the mouse position.
 * Prioritizes shared edge targets over single-widget targets.
 *
 * @param mouseX - Mouse X position normalized (0-1)
 * @param mouseY - Mouse Y position normalized (0-1)
 * @param layouts - Current layouts keyed by container ID
 * @param draggedContainerId - Container ID of the widget being dragged
 * @param inferenceState - Layout inference state (can be null for basic behavior)
 * @returns The enhanced drop target, or null if not hovering over any
 */
export function findEnhancedDropTarget(
    mouseX: number,
    mouseY: number,
    layouts: Record<string, NormalizedLayout>,
    draggedContainerId: string,
    inferenceState: LayoutInferenceState | null,
): EnhancedDropTarget | null {
    // First, check for shared edge targets (higher priority)
    if (inferenceState) {
        const sharedEdgeTarget = findSharedEdgeTarget(mouseX, mouseY, inferenceState, layouts, draggedContainerId);
        if (sharedEdgeTarget) {
            const { edge, column, row } = sharedEdgeTarget;

            // Determine direction based on which side of the edge the mouse is on
            let direction: DropDirection;
            let targetContainerId: string;

            if (edge.orientation === 'horizontal') {
                // Mouse above the edge → dropping into top of widgetB
                // Mouse below the edge → dropping into bottom of widgetA
                if (mouseY < edge.position) {
                    direction = 'bottom';
                    targetContainerId = edge.widgetA;
                } else {
                    direction = 'top';
                    targetContainerId = edge.widgetB;
                }
            } else {
                // Mouse left of the edge → dropping into right of widgetA
                // Mouse right of the edge → dropping into left of widgetB
                if (mouseX < edge.position) {
                    direction = 'right';
                    targetContainerId = edge.widgetA;
                } else {
                    direction = 'left';
                    targetContainerId = edge.widgetB;
                }
            }

            enhancedDropTargetResult.targetContainerId = targetContainerId;
            enhancedDropTargetResult.direction = direction;
            enhancedDropTargetResult.sharedEdge = edge;
            enhancedDropTargetResult.column = column;
            enhancedDropTargetResult.row = row;
            return enhancedDropTargetResult;
        }
    }

    // Fall back to standard drop target detection
    const basicTarget = findActiveDropTarget(mouseX, mouseY, layouts, draggedContainerId);
    if (basicTarget) {
        enhancedDropTargetResult.targetContainerId = basicTarget.targetContainerId;
        enhancedDropTargetResult.direction = basicTarget.direction;
        enhancedDropTargetResult.sharedEdge = null;
        enhancedDropTargetResult.column = null;
        enhancedDropTargetResult.row = null;

        // Check if target and dragged widget are in the same column/row
        // This handles the case where we're reordering within a column/row
        if (inferenceState) {
            const targetId = basicTarget.targetContainerId;
            const direction = basicTarget.direction;

            // For top/bottom drops, check if both widgets are in the same column
            if (direction === 'top' || direction === 'bottom') {
                const column = inferenceState.columns.find(
                    (c) => c.widgetIds.includes(targetId) && c.widgetIds.includes(draggedContainerId),
                );
                if (column) {
                    enhancedDropTargetResult.column = column;
                    // sharedEdge stays null - preview will use target + direction
                }
            }

            // For left/right drops, check if both widgets are in the same row
            if (direction === 'left' || direction === 'right') {
                const row = inferenceState.rows.find(
                    (r) => r.widgetIds.includes(targetId) && r.widgetIds.includes(draggedContainerId),
                );
                if (row) {
                    enhancedDropTargetResult.row = row;
                    // sharedEdge stays null - preview will use target + direction
                }
            }
        }

        return enhancedDropTargetResult;
    }

    return null;
}

// ========== N-WAY DROP PREVIEW COMPUTATION ==========
// These functions compute layouts for multi-widget redistribution.

/**
 * Compute the drop preview for a column drop operation.
 * Redistributes vertical space proportionally among all column members plus the new widget.
 *
 * @param column - The column being dropped into
 * @param draggedLayout - Original layout of the dragged widget
 * @param insertionEdge - The shared edge where insertion occurs
 * @param layouts - Current layouts keyed by container ID
 * @param draggedId - Container ID of the widget being dragged
 * @returns Multi-widget drop preview with layouts for all affected widgets
 */
export function computeColumnDropPreview(
    column: InferredColumn,
    draggedLayout: NormalizedLayout,
    insertionEdge: SharedEdge,
    layouts: Record<string, NormalizedLayout>,
    draggedId: string,
): MultiWidgetDropPreview {
    const result: Record<string, NormalizedLayout> = {};

    // Filter out the dragged widget from column members if it's already in the column
    const columnMembers = column.widgetIds.filter((id) => id !== draggedId);

    // Calculate total height of the column
    let totalHeight = 0;
    for (const id of columnMembers) {
        totalHeight += layouts[id].h;
    }

    // Determine insertion index
    const insertAfter = insertionEdge.widgetA;
    let insertionIndex = columnMembers.indexOf(insertAfter);
    if (insertionIndex === -1) {
        insertionIndex = columnMembers.indexOf(insertionEdge.widgetB);
        if (insertionIndex !== -1) {
            insertionIndex -= 1;
        }
    }
    // If still -1, insert at the beginning
    if (insertionIndex === -1) {
        insertionIndex = -1; // Will increment to 0
    }

    // Build new ordered list with dragged widget inserted
    const newOrder: string[] = [];
    for (let i = 0; i < columnMembers.length; i++) {
        newOrder.push(columnMembers[i]);
        if (i === insertionIndex) {
            newOrder.push(draggedId);
        }
    }
    // Handle insertion at the very beginning
    if (insertionIndex === -1) {
        newOrder.unshift(draggedId);
    }

    // Build current sizes array in new order (use average for the new widget)
    const avgHeight = totalHeight / columnMembers.length;
    const currentSizes = newOrder.map((id) => (id === draggedId ? avgHeight : layouts[id].h));

    // Distribute space proportionally with grid snapping
    const distributedSizes = distributeSpaceProportionally(totalHeight, currentSizes);

    // Snap column bounds
    const columnX = snapToMosaicGrid(column.x);
    const columnW = snapToMosaicGrid(column.xEnd - column.x);

    // Get column bounds and assign layouts
    const firstMember = layouts[columnMembers[0]];
    let currentY = snapToMosaicGrid(firstMember.y);

    for (let i = 0; i < newOrder.length; i++) {
        const id = newOrder[i];
        const h = distributedSizes[i];
        result[id] = {
            x: columnX,
            y: currentY,
            w: columnW,
            h,
        };
        currentY += h;
    }

    return { layouts: result };
}

/**
 * Compute the drop preview for a row drop operation.
 * Redistributes horizontal space proportionally among all row members plus the new widget.
 *
 * @param row - The row being dropped into
 * @param draggedLayout - Original layout of the dragged widget
 * @param insertionEdge - The shared edge where insertion occurs
 * @param layouts - Current layouts keyed by container ID
 * @param draggedId - Container ID of the widget being dragged
 * @returns Multi-widget drop preview with layouts for all affected widgets
 */
export function computeRowDropPreview(
    row: InferredRow,
    draggedLayout: NormalizedLayout,
    insertionEdge: SharedEdge,
    layouts: Record<string, NormalizedLayout>,
    draggedId: string,
): MultiWidgetDropPreview {
    const result: Record<string, NormalizedLayout> = {};

    // Filter out the dragged widget from row members if it's already in the row
    const rowMembers = row.widgetIds.filter((id) => id !== draggedId);

    // Calculate total width of the row
    let totalWidth = 0;
    for (const id of rowMembers) {
        totalWidth += layouts[id].w;
    }

    // Determine insertion index
    const insertAfter = insertionEdge.widgetA;
    let insertionIndex = rowMembers.indexOf(insertAfter);
    if (insertionIndex === -1) {
        insertionIndex = rowMembers.indexOf(insertionEdge.widgetB);
        if (insertionIndex !== -1) {
            insertionIndex -= 1;
        }
    }
    // If still -1, insert at the beginning
    if (insertionIndex === -1) {
        insertionIndex = -1; // Will increment to 0
    }

    // Build new ordered list with dragged widget inserted
    const newOrder: string[] = [];
    for (let i = 0; i < rowMembers.length; i++) {
        newOrder.push(rowMembers[i]);
        if (i === insertionIndex) {
            newOrder.push(draggedId);
        }
    }
    // Handle insertion at the very beginning
    if (insertionIndex === -1) {
        newOrder.unshift(draggedId);
    }

    // Build current sizes array in new order (use average for the new widget)
    const avgWidth = totalWidth / rowMembers.length;
    const currentSizes = newOrder.map((id) => (id === draggedId ? avgWidth : layouts[id].w));

    // Distribute space proportionally with grid snapping
    const distributedSizes = distributeSpaceProportionally(totalWidth, currentSizes);

    // Snap row bounds
    const rowY = snapToMosaicGrid(row.y);
    const rowH = snapToMosaicGrid(row.yEnd - row.y);

    // Get row bounds and assign layouts
    const firstMember = layouts[rowMembers[0]];
    let currentX = snapToMosaicGrid(firstMember.x);

    for (let i = 0; i < newOrder.length; i++) {
        const id = newOrder[i];
        const w = distributedSizes[i];
        result[id] = {
            x: currentX,
            y: rowY,
            w,
            h: rowH,
        };
        currentX += w;
    }

    return { layouts: result };
}

/**
 * Compute the enhanced drop preview for any drop target.
 * Handles both shared edge targets (N-way redistribution) and standard targets (2-way split).
 *
 * @param target - The enhanced drop target
 * @param draggedLayout - Original layout of the dragged widget
 * @param layouts - Current layouts keyed by container ID
 * @param draggedId - Container ID of the widget being dragged
 * @returns Either a MultiWidgetDropPreview (for N-way) or DropPreviewLayout (for 2-way)
 */
export function computeEnhancedDropPreview(
    target: EnhancedDropTarget,
    draggedLayout: NormalizedLayout,
    layouts: Record<string, NormalizedLayout>,
    draggedId: string,
): MultiWidgetDropPreview | DropPreviewLayout {
    // If we have a column and shared edge, use N-way column redistribution
    if (target.column && target.sharedEdge) {
        return computeColumnDropPreview(target.column, draggedLayout, target.sharedEdge, layouts, draggedId);
    }

    // If we have a row and shared edge, use N-way row redistribution
    if (target.row && target.sharedEdge) {
        return computeRowDropPreview(target.row, draggedLayout, target.sharedEdge, layouts, draggedId);
    }

    // Fall back to standard 2-way drop preview
    const targetLayout = layouts[target.targetContainerId];
    return computeDropPreview(targetLayout, target.direction, draggedLayout);
}

/**
 * Type guard to check if a preview is a MultiWidgetDropPreview.
 */
export function isMultiWidgetPreview(
    preview: MultiWidgetDropPreview | DropPreviewLayout,
): preview is MultiWidgetDropPreview {
    return 'layouts' in preview && !('draggedLayout' in preview);
}

// ========== PRECOMPUTED DROP SCENARIOS ==========
// All possible drop outcomes are computed at drag start for efficient lookup.

/**
 * Compute column redistribution preview given target widget and direction.
 * Used for reordering within a column (when dragged widget is part of the column).
 */
function computeColumnPreviewByTarget(
    column: InferredColumn,
    targetId: string,
    direction: 'top' | 'bottom',
    layouts: Record<string, NormalizedLayout>,
    draggedId: string,
): Record<string, NormalizedLayout> {
    const result: Record<string, NormalizedLayout> = {};

    // Filter out the dragged widget from column members
    const columnMembers = column.widgetIds.filter((id) => id !== draggedId);

    // Calculate total height of the column (including dragged widget)
    let totalHeight = layouts[draggedId].h;
    for (const id of columnMembers) {
        totalHeight += layouts[id].h;
    }

    // Determine insertion position based on target and direction
    const targetIndex = columnMembers.indexOf(targetId);
    let insertionIndex: number;
    if (direction === 'top') {
        // Insert before target
        insertionIndex = targetIndex - 1; // -1 means insert at beginning
    } else {
        // Insert after target
        insertionIndex = targetIndex;
    }

    // Build new order with dragged widget inserted
    const newOrder: string[] = [];
    for (let i = 0; i < columnMembers.length; i++) {
        if (i === 0 && insertionIndex === -1) {
            newOrder.push(draggedId);
        }
        newOrder.push(columnMembers[i]);
        if (i === insertionIndex && insertionIndex >= 0) {
            newOrder.push(draggedId);
        }
    }
    // Handle insertion at the very end
    if (insertionIndex >= columnMembers.length) {
        newOrder.push(draggedId);
    }

    // Build current sizes array in new order
    const currentSizes = newOrder.map((id) => layouts[id].h);

    // Distribute space proportionally with grid snapping
    const distributedSizes = distributeSpaceProportionally(totalHeight, currentSizes);

    // Snap column bounds
    const columnX = snapToMosaicGrid(column.x);
    const columnW = snapToMosaicGrid(column.xEnd - column.x);

    // Get column bounds and assign layouts
    const firstMember = layouts[column.widgetIds[0]];
    let currentY = snapToMosaicGrid(firstMember.y);

    for (let i = 0; i < newOrder.length; i++) {
        const id = newOrder[i];
        const h = distributedSizes[i];
        result[id] = {
            x: columnX,
            y: currentY,
            w: columnW,
            h,
        };
        currentY += h;
    }

    return result;
}

/**
 * Compute row redistribution preview given target widget and direction.
 * Used for reordering within a row (when dragged widget is part of the row).
 */
function computeRowPreviewByTarget(
    row: InferredRow,
    targetId: string,
    direction: 'left' | 'right',
    layouts: Record<string, NormalizedLayout>,
    draggedId: string,
): Record<string, NormalizedLayout> {
    const result: Record<string, NormalizedLayout> = {};

    // Filter out the dragged widget from row members
    const rowMembers = row.widgetIds.filter((id) => id !== draggedId);

    // Calculate total width of the row (including dragged widget)
    let totalWidth = layouts[draggedId].w;
    for (const id of rowMembers) {
        totalWidth += layouts[id].w;
    }

    // Determine insertion position based on target and direction
    const targetIndex = rowMembers.indexOf(targetId);
    let insertionIndex: number;
    if (direction === 'left') {
        // Insert before target
        insertionIndex = targetIndex - 1; // -1 means insert at beginning
    } else {
        // Insert after target
        insertionIndex = targetIndex;
    }

    // Build new order with dragged widget inserted
    const newOrder: string[] = [];
    for (let i = 0; i < rowMembers.length; i++) {
        if (i === 0 && insertionIndex === -1) {
            newOrder.push(draggedId);
        }
        newOrder.push(rowMembers[i]);
        if (i === insertionIndex && insertionIndex >= 0) {
            newOrder.push(draggedId);
        }
    }
    // Handle insertion at the very end
    if (insertionIndex >= rowMembers.length) {
        newOrder.push(draggedId);
    }

    // Build current sizes array in new order
    const currentSizes = newOrder.map((id) => layouts[id].w);

    // Distribute space proportionally with grid snapping
    const distributedSizes = distributeSpaceProportionally(totalWidth, currentSizes);

    // Snap row bounds
    const rowY = snapToMosaicGrid(row.y);
    const rowH = snapToMosaicGrid(row.yEnd - row.y);

    // Get row bounds and assign layouts
    const firstMember = layouts[row.widgetIds[0]];
    let currentX = snapToMosaicGrid(firstMember.x);

    for (let i = 0; i < newOrder.length; i++) {
        const id = newOrder[i];
        const w = distributedSizes[i];
        result[id] = {
            x: currentX,
            y: rowY,
            w,
            h: rowH,
        };
        currentX += w;
    }

    return result;
}

/**
 * Compute 2-way split preview as a result layout map.
 */
function compute2WaySplitLayouts(
    targetLayout: NormalizedLayout,
    direction: DropDirection,
    draggedLayout: NormalizedLayout,
    targetId: string,
    draggedId: string,
): Record<string, NormalizedLayout> {
    const preview = computeDropPreview(targetLayout, direction, draggedLayout);
    return {
        [draggedId]: preview.draggedLayout,
        [targetId]: preview.targetLayout,
    };
}

/**
 * Compute column insertion preview when dragging from outside a column.
 * New widget gets proportional share, all sizes snapped to mosaic grid.
 */
function computeColumnInsertByTarget(
    column: InferredColumn,
    targetId: string,
    direction: 'top' | 'bottom',
    layouts: Record<string, NormalizedLayout>,
    draggedId: string,
): Record<string, NormalizedLayout> {
    const result: Record<string, NormalizedLayout> = {};
    const columnMembers = column.widgetIds;

    // Calculate total height
    let totalHeight = 0;
    for (const id of columnMembers) {
        totalHeight += layouts[id].h;
    }

    // Determine insertion position based on target and direction
    const targetIndex = columnMembers.indexOf(targetId);
    const insertionIndex = direction === 'top' ? targetIndex - 1 : targetIndex;

    // Build new order with dragged widget inserted
    const newOrder: string[] = [];
    for (let i = 0; i < columnMembers.length; i++) {
        if (i === 0 && insertionIndex === -1) {
            newOrder.push(draggedId);
        }
        newOrder.push(columnMembers[i]);
        if (i === insertionIndex && insertionIndex >= 0) {
            newOrder.push(draggedId);
        }
    }
    if (insertionIndex >= columnMembers.length) {
        newOrder.push(draggedId);
    }

    // Build current sizes array in new order (use average for the new widget)
    const avgHeight = totalHeight / columnMembers.length;
    const currentSizes = newOrder.map((id) => (id === draggedId ? avgHeight : layouts[id].h));

    // Distribute space proportionally with grid snapping
    const distributedSizes = distributeSpaceProportionally(totalHeight, currentSizes);

    // Snap column bounds
    const columnX = snapToMosaicGrid(column.x);
    const columnW = snapToMosaicGrid(column.xEnd - column.x);

    // Get column bounds and assign layouts
    let currentY = snapToMosaicGrid(layouts[columnMembers[0]].y);
    for (let i = 0; i < newOrder.length; i++) {
        const id = newOrder[i];
        const h = distributedSizes[i];
        result[id] = { x: columnX, y: currentY, w: columnW, h };
        currentY += h;
    }

    return result;
}

/**
 * Compute row insertion preview when dragging from outside a row.
 * New widget gets proportional share, all sizes snapped to mosaic grid.
 */
function computeRowInsertByTarget(
    row: InferredRow,
    targetId: string,
    direction: 'left' | 'right',
    layouts: Record<string, NormalizedLayout>,
    draggedId: string,
): Record<string, NormalizedLayout> {
    const result: Record<string, NormalizedLayout> = {};
    const rowMembers = row.widgetIds;

    // Calculate total width
    let totalWidth = 0;
    for (const id of rowMembers) {
        totalWidth += layouts[id].w;
    }

    // Determine insertion position based on target and direction
    const targetIndex = rowMembers.indexOf(targetId);
    const insertionIndex = direction === 'left' ? targetIndex - 1 : targetIndex;

    // Build new order with dragged widget inserted
    const newOrder: string[] = [];
    for (let i = 0; i < rowMembers.length; i++) {
        if (i === 0 && insertionIndex === -1) {
            newOrder.push(draggedId);
        }
        newOrder.push(rowMembers[i]);
        if (i === insertionIndex && insertionIndex >= 0) {
            newOrder.push(draggedId);
        }
    }
    if (insertionIndex >= rowMembers.length) {
        newOrder.push(draggedId);
    }

    // Build current sizes array in new order (use average for the new widget)
    const avgWidth = totalWidth / rowMembers.length;
    const currentSizes = newOrder.map((id) => (id === draggedId ? avgWidth : layouts[id].w));

    // Distribute space proportionally with grid snapping
    const distributedSizes = distributeSpaceProportionally(totalWidth, currentSizes);

    // Snap row bounds
    const rowY = snapToMosaicGrid(row.y);
    const rowH = snapToMosaicGrid(row.yEnd - row.y);

    // Get row bounds and assign layouts
    let currentX = snapToMosaicGrid(layouts[rowMembers[0]].x);
    for (let i = 0; i < newOrder.length; i++) {
        const id = newOrder[i];
        const w = distributedSizes[i];
        result[id] = { x: currentX, y: rowY, w, h: rowH };
        currentX += w;
    }

    return result;
}

/**
 * Generate a scenario key from target ID and direction.
 */
export function getScenarioKey(targetId: string, direction: DropDirection): string {
    return `${targetId}:${direction}`;
}

/**
 * Precompute all possible drop scenarios for a drag operation.
 * Called once at drag start; results are looked up during drag move.
 *
 * @param layouts - Current layouts keyed by container ID
 * @param draggedId - Container ID of the widget being dragged
 * @param inferenceState - Layout inference state with columns/rows
 * @returns All precomputed drop scenarios
 */
export function precomputeDropScenarios(
    layouts: Record<string, NormalizedLayout>,
    draggedId: string,
    inferenceState: LayoutInferenceState,
): PrecomputedDropScenarios {
    const scenarios = new Map<string, DropScenario>();
    const draggedLayout = layouts[draggedId];

    // Find which column/row the dragged widget belongs to
    const draggedColumn = inferenceState.columns.find((c) => c.widgetIds.includes(draggedId));
    const draggedRow = inferenceState.rows.find((r) => r.widgetIds.includes(draggedId));

    // For each potential target widget
    for (const targetId of Object.keys(layouts)) {
        if (targetId === draggedId) continue;

        const targetLayout = layouts[targetId];

        // Check if target is in the same column/row as dragged widget
        const inSameColumn = draggedColumn?.widgetIds.includes(targetId) ?? false;
        const inSameRow = draggedRow?.widgetIds.includes(targetId) ?? false;

        // Find which column/row the target widget belongs to (even if different from dragged)
        const targetColumn = inferenceState.columns.find((c) => c.widgetIds.includes(targetId));
        const targetRow = inferenceState.rows.find((r) => r.widgetIds.includes(targetId));

        // Compute scenario for each direction
        const directions: DropDirection[] = ['top', 'bottom', 'left', 'right', 'center'];

        for (const direction of directions) {
            let resultLayouts: Record<string, NormalizedLayout>;

            if (direction === 'center') {
                // Center = swap positions
                resultLayouts = {
                    [draggedId]: { ...targetLayout },
                    [targetId]: { ...draggedLayout },
                };
            } else if ((direction === 'top' || direction === 'bottom') && inSameColumn && draggedColumn) {
                // Reorder within column - keep proportions
                resultLayouts = computeColumnPreviewByTarget(draggedColumn, targetId, direction, layouts, draggedId);
            } else if ((direction === 'top' || direction === 'bottom') && targetColumn) {
                // Insert into column from outside - 1/(N+1) redistribution
                resultLayouts = computeColumnInsertByTarget(targetColumn, targetId, direction, layouts, draggedId);
            } else if ((direction === 'left' || direction === 'right') && inSameRow && draggedRow) {
                // Reorder within row - keep proportions
                resultLayouts = computeRowPreviewByTarget(draggedRow, targetId, direction, layouts, draggedId);
            } else if ((direction === 'left' || direction === 'right') && targetRow) {
                // Insert into row from outside - 1/(N+1) redistribution
                resultLayouts = computeRowInsertByTarget(targetRow, targetId, direction, layouts, draggedId);
            } else {
                // Standard 2-way split
                resultLayouts = compute2WaySplitLayouts(targetLayout, direction, draggedLayout, targetId, draggedId);
            }

            const key = getScenarioKey(targetId, direction);
            scenarios.set(key, {
                targetContainerId: targetId,
                direction,
                resultLayouts,
            });
        }
    }

    return { scenarios, draggedId };
}

/**
 * Look up a precomputed drop scenario.
 *
 * @param scenarios - Precomputed scenarios from precomputeDropScenarios
 * @param targetId - Target widget ID
 * @param direction - Drop direction
 * @returns The scenario, or undefined if not found
 */
export function getDropScenario(
    scenarios: PrecomputedDropScenarios,
    targetId: string,
    direction: DropDirection,
): DropScenario | undefined {
    return scenarios.scenarios.get(getScenarioKey(targetId, direction));
}
